// One view grows into the next (PLAN-2026-10-17 §5, B-400, B-404): a screen
// change drawn as the same page changing shape, or as the look's own way of
// turning a page, rather than a cut.
//
// THE VIEW TRANSITIONS API, same-document: the browser keeps a picture of
// each named part (`view-transition-name`, in the stylesheet) before the
// change and after it, and moves one into the other — the roster's panel
// into the dock, the tool's into the room, a front page's section into its
// screen, a team's name into its page's heading (the *hero*). Everything
// else on the screen enters as the look moves (`data-motion`: deeper, back,
// or the page changing shape), and the top bar and the rail hold still.
// Where the browser has no view transitions, or the person asks for less
// motion, the change is simply made, as it always was.
//
// WHEN THE NEW SHAPE IS DRAWN, the frame says so (`useMorphLanded`, in
// `AppShell.tsx`): the router draws a new address as a transition, after it
// hears it, which for Back is a moment later still. Until then the page
// shows its old shape, as it would with no morph at all; a change that never
// lands lets the transition go after two seconds rather than holding it.

import { useLayoutEffect } from 'react';
import { useLocation } from 'react-router-dom';

type Transitioning = Document & {
  startViewTransition?: (update: () => Promise<void>) => { finished?: Promise<void> } | undefined;
};

/** How the screen moves: in from ahead, back from behind, or changing shape in place. */
export type Motion = 'deeper' | 'back' | 'shape';

let landed: (() => void) | null = null;
/** A morph's change is being made: the phone's slide stands aside (B-313). */
let morphing = false;

/** Whether the screen change under way is a morph's. */
export function isMorphing(): boolean {
  return morphing;
}

/**
 * Make the change `go` makes as a morph. `hero` is the thing pressed, which
 * grows into the new screen's `.fsp-hero-target` (a team's name into its
 * page's heading).
 */
export function morph(
  go: () => void,
  { motion = 'shape', hero = null }: { motion?: Motion; hero?: HTMLElement | null } = {},
): void {
  const doc = document as Transitioning;
  const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  if (typeof doc.startViewTransition !== 'function' || still) {
    go();
    return;
  }
  const root = document.documentElement;
  root.dataset.motion = motion;
  if (hero !== null) hero.style.viewTransitionName = 'fsp-hero';
  const tidy = () => {
    delete root.dataset.motion;
    delete root.dataset.hero;
  };
  const transition = doc.startViewTransition(
    () =>
      new Promise<void>((resolve) => {
        const done = () => {
          if (landed === land) landed = null;
          morphing = false;
          resolve();
        };
        // A HERO WAITS A MOMENT FOR WHERE IT LANDS: a screen whose read is
        // on its way draws its target a little after its address changes
        // (a matchup not yet read), and a hero with nowhere to land fades
        // rather than grows (the review of B-409).
        const land = hero === null ? done : () => untilTarget(done);
        landed = land;
        morphing = true;
        // The old picture is taken: the name moves to where it is going.
        if (hero !== null) {
          hero.style.viewTransitionName = '';
          root.dataset.hero = '';
        }
        window.setTimeout(done, 2000);
        go();
      }),
  );
  if (transition?.finished) transition.finished.then(tidy, tidy);
  else window.setTimeout(tidy, 2500);
}

/** How long a hero waits for its target once the new screen is up, in ms. */
const HERO_WAIT = 700;

function untilTarget(done: () => void): void {
  const from = performance.now();
  const look = () => {
    if (document.querySelector('.fsp-hero-target') !== null || performance.now() - from > HERO_WAIT)
      done();
    // A timer, not a frame: the browser draws no frames while a transition
    // waits for its new picture, so a frame's callback would wait it out.
    else window.setTimeout(look, 16);
  };
  look();
}

/** The new screen is drawn: a morph to it can run. The frame's, once. */
export function useMorphLanded(): void {
  const { key } = useLocation();
  useLayoutEffect(() => {
    landed?.();
  }, [key]);
}
